/**
 * The one spelling a boolean takes while it is a value pill, and the one decode back out (ACT2-558).
 *
 * **Why this is a shared module rather than two matching literals.** A boolean leaf's pills are built
 * on the BACKEND (`rows.ts` `buildPills`, from `observed.topValues`) and clicked on the FRONTEND
 * (`insights/InsightPanel.tsx`, which writes the value back into the definition). If the two ends ever
 * disagree about the spelling the failure is silent in both directions: nothing renders as selected,
 * and a click writes a value the compiler reads as a string. `compile.ts` emits a bare `TRUE` for a
 * JSON boolean and binds a quoted `'true'` for a string, so `IS_TRIAL = 'true'` compiles, runs, and
 * matches nothing. The frontend reaches this file through the `@server/audience-boolean-pill` alias —
 * the repo's stated preference over re-implementing shared logic on the other side of the wire.
 *
 * **Why the ENCODE accepts more than it emits.** A boolean reaches `topValues` by three routes, and
 * they do not agree:
 *
 *  - Snowflake `APPROX_TOP_K` on a BOOLEAN column → a JSON `true` / `false`;
 *  - the Postgres SCAN path (`buildPgTopKSql` → node-postgres) → a JS `true` / `false`;
 *  - the Postgres CATALOG-STATS path (`measureFromCatalogStats`, tried FIRST for Postgres) reads
 *    `pg_stats.most_common_vals::text` and parses it as `string[]` — and Postgres renders a boolean
 *    array as `{f,t}`. Verified against a live engine: `ANALYZE` on a boolean column yields
 *    `most_common_vals` = `{f,t}`, `most_common_freqs` = `{0.9,0.1}`.
 *
 * So `'t'` is not an exotic input, it is what the DEFAULT Postgres path produces. Normalising on READ
 * (rather than at the probe's write) also repairs every sketch already stored in that spelling.
 *
 * **Why the DECODE declines instead of answering `false`.** A decoder typed `boolean` maps `'t'`,
 * `'TRUE'` and `'1'` all to a confident `false`, and the caller cannot tell that apart from a real
 * `false` — so an unrecognised spelling writes the INVERSE of what the customer clicked. Returning
 * `undefined` makes "I do not recognise this" a state the caller has to handle, and the caller
 * declines the click rather than committing a guess.
 *
 * Layer: shared leaf. No imports, so it stays cheap to bundle into the SPA.
 */

export const BOOLEAN_PILL_TRUE = 'true';
export const BOOLEAN_PILL_FALSE = 'false';

/**
 * A warehouse-supplied value → its pill spelling, or `undefined` when the value is not a boolean this
 * module recognises.
 *
 * `'1'` / `'0'` / `'yes'` are deliberately NOT accepted. No engine we read renders a boolean that way,
 * so accepting them would be inventing a meaning for a value we do not understand — and the value it
 * invents writes audience membership. A declined value simply gets no pill.
 */
export function booleanPillSpelling(value: unknown): string | undefined {
  if (typeof value === 'boolean') return value ? BOOLEAN_PILL_TRUE : BOOLEAN_PILL_FALSE;
  if (typeof value !== 'string') return undefined;
  const normalized = value.toLowerCase();
  if (normalized === 'true' || normalized === 't') return BOOLEAN_PILL_TRUE;
  if (normalized === 'false' || normalized === 'f') return BOOLEAN_PILL_FALSE;
  return undefined;
}

/**
 * A pill spelling → the JSON boolean the definition stores, or `undefined` when the caller must
 * decline the click.
 *
 * Strict on purpose — it accepts only what {@link booleanPillSpelling} emits. Every widening belongs
 * on the ENCODE side, where an unrecognised value costs a missing pill; here it would cost a wrong
 * rule.
 */
export function booleanFromPill(raw: string): boolean | undefined {
  if (raw === BOOLEAN_PILL_TRUE) return true;
  if (raw === BOOLEAN_PILL_FALSE) return false;
  return undefined;
}
